/**
 * SOP Service
 * Handles SOP-related API calls
 */

import axios from 'axios';
import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import { SOPAdapter } from '@/adapters';
import type {
	ApiResponse,
	SOP,
	SOPDto,
	SOPResponseDto,
	SOPApprovalResponseDto,
	SOPsListResponseDto,
	SOPsListQueryParams,
	ApproveRejectSOPDto,
	CreateSOPDto,
	SOPActiveDecisionSession,
	SOPDecisionHistory,
	VectorSyncResult
} from '@/types';

/** A ticket the SOP pipeline mapped to an SOP. */
export interface SopTicketListItem {
	ticketId: string;
	subject: string | null;
	summary: string | null;
	customer: { name: string | null; email: string | null } | null;
	channel: string | null;
	resolvedAt: string | null;
}

export interface SopTicketPage {
	sopId: string;
	tickets: SopTicketListItem[];
	offset: number;
	limit: number;
	hasMore: boolean;
}

interface SopMergeApiResult {
	sop: SOPDto;
	deprecatedSopId: string;
	deprecatedIntent: string;
	relationship: 'same' | 'subtype';
}

export interface SopMergeResult {
	sop: SOP;
	deprecatedSopId: string;
	deprecatedIntent: string;
	relationship: 'same' | 'subtype';
}

export const sopService = {
	async createSOP(data: CreateSOPDto, scope: { bu_id?: string } = {}): Promise<SOP> {
		const payload = SOPAdapter.toCreateRequest(data);
		const response = await api.post<ApiResponse<SOPApprovalResponseDto>>(
			withBuScope(API_ENDPOINTS.SOPS.BASE, scope),
			payload
		);
		return SOPAdapter.fromDto(response.data.data.sop);
	},

	async getSOPs(params?: SOPsListQueryParams): Promise<{
		sops: SOP[];
		pagination: { page: number; totalPages: number; total: number };
	}> {
		const response = await api.get<ApiResponse<SOPsListResponseDto>>(API_ENDPOINTS.SOPS.BASE, { params });
		return SOPAdapter.fromListApi(response.data.data);
	},

	async getSOPById(id: string): Promise<{ sop: SOP; previewUrl: string; draftPreviewUrl?: string }> {
		const response = await api.get<ApiResponse<SOPResponseDto>>(API_ENDPOINTS.SOPS.BY_ID(id));
		return SOPAdapter.fromDetailApi(response.data.data);
	},

	// Uses plain axios — presigned S3 URLs embed auth in query params and reject Authorization headers
	async getMarkdownContent(previewUrl: string): Promise<string> {
		const response = await axios.get<string>(previewUrl, { responseType: 'text' });
		return response.data;
	},

	/** One page of the tickets mapped to the SOP, newest resolved first. */
	async getTickets(id: string, offset: number, limit: number): Promise<SopTicketPage> {
		const response = await api.get<ApiResponse<SopTicketPage>>(API_ENDPOINTS.SOPS.TICKETS(id), {
			params: { offset, limit }
		});
		return response.data.data;
	},

	/** Every past ticket in the SOP's own subcategory — the pool the bucketing widens to. */
	async getSubcategoryTickets(id: string, offset: number, limit: number): Promise<SopTicketPage> {
		const response = await api.get<ApiResponse<SopTicketPage>>(API_ENDPOINTS.SOPS.SUBCATEGORY_TICKETS(id), {
			params: { offset, limit }
		});
		return response.data.data;
	},

	async updateContent(
		id: string,
		content: string,
		options?: { expectedContentHash?: string }
	): Promise<{ sop: SOP; draftPreviewUrl?: string }> {
		const body: { content: string; expectedContentHash?: string } = { content };
		// Optimistic-concurrency guard — the backend rejects (409) if the draft changed under us.
		if (options?.expectedContentHash) body.expectedContentHash = options.expectedContentHash;
		const response = await api.patch<ApiResponse<SOPApprovalResponseDto>>(API_ENDPOINTS.SOPS.CONTENT(id), body);
		return {
			sop: SOPAdapter.fromDto(response.data.data.sop),
			...(response.data.data.draftPreviewUrl && { draftPreviewUrl: response.data.data.draftPreviewUrl })
		};
	},

	async approveRejectSOP(id: string, data: ApproveRejectSOPDto): Promise<SOP> {
		const response = await api.patch<ApiResponse<SOPApprovalResponseDto>>(API_ENDPOINTS.SOPS.APPROVAL(id), data);
		return SOPAdapter.fromDto(response.data.data.sop);
	},

	/** (Re)build the protocol for an approved SOP; the SOP's workflowStatus reports progress. */
	async buildProtocol(id: string): Promise<void> {
		await api.post(API_ENDPOINTS.SOPS.BUILD_PROTOCOL(id), {});
	},

	/** Rebuilds the protocol with the SOP added; waits for the build, which takes about a minute. */
	async foldIntoProtocol(id: string, workflowId: string): Promise<{ workflowId: string; name: string }> {
		const response = await api.post<ApiResponse<{ workflowId: string; name: string }>>(
			API_ENDPOINTS.SOPS.FOLD(id),
			{ workflowId },
			{ timeout: 240_000 }
		);
		return response.data.data;
	},

	async convertToArticle(id: string): Promise<{ sourceId: string; name: string }> {
		const response = await api.post<ApiResponse<{ sourceId: string; name: string }>>(API_ENDPOINTS.SOPS.ARTICLE(id), {});
		return response.data.data;
	},

	/** The compile run in flight for this SOP's protocol, or null if none. */
	async getCurrentDecisionSession(id: string): Promise<SOPActiveDecisionSession | null> {
		const response = await api.get<ApiResponse<{ session: SOPActiveDecisionSession | null }>>(
			`/sops/${id}/decisions/current`
		);
		return response.data.data.session ?? null;
	},

	/**
	 * Every compile of this SOP's protocol: the questions asked and how each was answered
	 * (by a person, settled automatically, or carried over). Empty when never compiled.
	 */
	async getDecisionHistory(id: string): Promise<SOPDecisionHistory> {
		const response = await api.get<ApiResponse<SOPDecisionHistory>>(`/sops/${id}/decisions/history`);
		return response.data.data;
	},

	async deleteSOP(id: string): Promise<void> {
		await api.delete(API_ENDPOINTS.SOPS.BY_ID(id));
	},


	async resyncSOP(id: string, dryRun = false): Promise<VectorSyncResult> {
		const response = await api.post<ApiResponse<VectorSyncResult>>(
			API_ENDPOINTS.SOPS.RESYNC(id),
			{},
			{ params: dryRun ? { dryRun: true } : undefined }
		);
		return response.data.data;
	},

	async updateCategorization(id: string, category: string, subcategory: string): Promise<SOP> {
		const response = await api.patch<ApiResponse<SOPApprovalResponseDto>>(API_ENDPOINTS.SOPS.CATEGORIZATION(id), {
			category,
			subcategory
		});
		return SOPAdapter.fromDto(response.data.data.sop);
	},

	async rename(id: string, intent: string): Promise<SOP> {
		const response = await api.patch<ApiResponse<SOPApprovalResponseDto>>(API_ENDPOINTS.SOPS.RENAME(id), { intent });
		return SOPAdapter.fromDto(response.data.data.sop);
	},

	/**
	 * Merge two catalog SOPs the automated dedup missed. `survivingId` stays canonical and absorbs
	 * `mergedId`'s content; `mergedId` is deprecated with a redirect to the survivor. Pairwise and
	 * synchronous — for merging more than two SOPs from the UI, use `mergeBatch` instead, which
	 * runs the N-way merge in the background on the server.
	 */
	async merge(data: { survivingId: string; mergedId: string; relationship?: 'same' | 'subtype' }): Promise<SopMergeResult> {
		const response = await api.post<ApiResponse<SopMergeApiResult>>(API_ENDPOINTS.SOPS.MERGE, data);
		return {
			sop: SOPAdapter.fromDto(response.data.data.sop),
			deprecatedSopId: response.data.data.deprecatedSopId,
			deprecatedIntent: response.data.data.deprecatedIntent,
			relationship: response.data.data.relationship
		};
	},

	/**
	 * Merge N SOPs (N >= 2) into one, in the background. `sopIds[0]` stays canonical. Returns as
	 * soon as the server has validated the request and flagged every SOP `mergeStatus: 'merging'`
	 * — NOT when the merge finishes. Progress/outcome are read back from `mergeStatus`/`mergeError`
	 * on the survivor via the normal SOP list/detail fetches — poll while any row shows 'merging'.
	 */
	async mergeBatch(data: { sopIds: string[]; finalName: string }): Promise<{ survivingId: string; mergedIds: string[] }> {
		const response = await api.post<ApiResponse<{ survivingId: string; mergedIds: string[] }>>(
			API_ENDPOINTS.SOPS.MERGE_BATCH,
			data
		);
		return response.data.data;
	},

	// Category/subcategory tree for the Sources sidebar — derived from the same Pulse
	// ticket-categorization data as /pulse/subcategory-counts, filtered to only
	// subcategories with a SOP or Knowledge doc attached. The "Add to category" picker
	// sources from the CS Categories catalog instead (see AddToCategoryDialog), not this.
	// drafts: 'true' counts only what the Drafts to review list shows.
	async getCategoryTree(params: { bu_id?: string; drafts?: 'true' } = {}): Promise<SopCategoryTreeItem[]> {
		const response = await api.get<ApiResponse<{ categories: SopCategoryTreeItem[] }>>(
			API_ENDPOINTS.SOPS.CATEGORIZATION_TREE,
			{ params }
		);
		return response.data.data.categories;
	}
};

export interface SopCategoryTreeSubcategory {
	name: string;
	sopCatId: string | null;
	status: 'none' | 'generating' | 'draft' | 'published';
	count: number;
}

export interface SopCategoryTreeItem {
	category: string;
	subcategories: SopCategoryTreeSubcategory[];
}
